import { useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { FACES, FACE_BY_ID, type FaceId } from "~/lib/faces";
import { MeterBar } from "~/ui/Chrome/MeterBar";
import { HoveredHint } from "~/ui/Chrome/HoveredHint";
import { MobileFaceStrip } from "~/ui/Chrome/MobileFaceStrip";
import { TrustUnlocked } from "~/ui/TrustUnlocked";
import { EasterEggReveal } from "~/ui/EasterEggReveal";
import { useKonamiCode } from "~/lib/hooks/useKonamiCode";
import { useVisitor } from "~/lib/store";

export function Root() {
  const { pathname, hash } = useLocation();

  // Scroll to top on every route change so a new screen always opens
  // from its header, not wherever the previous page was scrolled to.
  // Skip when there's a hash so #anchor links still jump to their target.
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [pathname, hash]);

  const slug = pathname === "/" ? null : pathname.slice(1);
  const activeFaceId: FaceId | null =
    slug && slug in FACE_BY_ID ? (slug as FaceId) : null;
  const activeId = slug;
  const showFaceStrip = pathname !== "/" && !pathname.startsWith("/tutorial");

  const facesVisitedCount = useVisitor((s) => s.facesVisited.length);
  const easterEggUnlocked = useVisitor((s) => s.easterEggUnlocked);
  const unlockEasterEgg = useVisitor((s) => s.unlockEasterEgg);

  useKonamiCode(() => {
    // Only honour the unlock once all eight cubies are explored — the
    // egg is the reward for completion, not a freebie shortcut.
    if (facesVisitedCount >= 8 && !easterEggUnlocked) {
      unlockEasterEgg();
    }
  });

  return (
    <div className="relative flex min-h-screen flex-col bg-jg-bg text-jg-fg">
      <MeterBar />
      <HoveredHint />
      <TrustUnlocked />
      <EasterEggReveal />
      <header className="fixed inset-x-0 top-0 z-50 flex items-center justify-between border-b border-jg-border/40 bg-jg-bg/60 px-4 py-3 backdrop-blur-md sm:px-6">
        <Link
          to="/"
          className="font-display text-lg font-semibold tracking-tight"
          aria-label="JGson home"
        >
          JG<span className="text-jg-augment">son</span>
        </Link>

        <nav
          aria-label="Faces of the JGson Core"
          className="hidden items-center gap-0.5 md:flex"
        >
          {FACES.map((f) => (
            <Link
              key={f.id}
              to={f.route}
              className={[
                "rounded-full px-2.5 py-1 text-xs transition-colors lg:text-sm",
                activeId === f.id
                  ? "bg-jg-bg-soft text-jg-fg"
                  : "text-jg-fg-muted hover:text-jg-fg",
              ].join(" ")}
              style={
                activeId === f.id ? { color: `var(${f.themeVar})` } : undefined
              }
            >
              <span className="hidden lg:inline">{f.label}</span>
              <span className="lg:hidden">{f.shortLabel}</span>
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/tutorial"
            aria-current={pathname.startsWith("/tutorial") ? "page" : undefined}
            className={[
              "rounded-full px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-jg-ai",
              pathname.startsWith("/tutorial")
                ? "bg-jg-ai/10 text-jg-ai"
                : "text-jg-fg-muted hover:text-jg-fg",
            ].join(" ")}
          >
            Tutorial
          </Link>
          <Link
            to="/hire"
            aria-current={pathname === "/hire" ? "page" : undefined}
            className={[
              "rounded-full px-4 py-1.5 text-sm font-medium transition-opacity",
              pathname === "/hire"
                ? "bg-jg-fg-muted text-jg-bg"
                : "bg-jg-fg text-jg-bg hover:opacity-90",
            ].join(" ")}
          >
            Hire
          </Link>
        </div>
      </header>

      <main className="flex-1 pt-16">
        {showFaceStrip && <MobileFaceStrip activeId={activeFaceId} />}
        <Outlet />
      </main>

      <footer className="border-t border-jg-border/40 px-4 py-6 text-center text-xs text-jg-fg-muted sm:px-6">
        © {new Date().getFullYear()} JGson · Built by João Vidotto ·{" "}
        <a
          href="mailto:joao@jgson.net"
          className="underline-offset-2 hover:underline"
        >
          joao@jgson.net
        </a>
      </footer>
    </div>
  );
}
